/**
 * Landing — Day 0 Attilius standalone public hero.
 *
 * Shown at the site root ("/") to unauthenticated visitors. The "Sign in"
 * nav link and CTAs route into the existing /login sign-in card, which is
 * left untouched. Authenticated visitors are routed to their role dashboard
 * by RootRoute before this ever renders.
 *
 * Visual canon: cream background #FAFAF7 (MI6.softWhite), near-black text
 * #0B0B0F (MI6.black), red accent #DC2626 (MI6.red). The locked PNG lockup
 * `/brand/mi6-attilius-horizontal.png` is the official brand mark in the nav.
 * Playfair Display for the serif display tagline (loaded globally in
 * index.html), Inter for body copy.
 * No exclamation points. No emojis. M-PACT hyphenated.
 *
 * Day 0 — 2026-07-08
 */

const BG = "#FAFAF7";
const RED = "#DC2626";
const BLACK = "#0B0B0F";
const WHITE = "#FFFFFF";
const BODY = "#525258";
const MUTED = "#6B6B70";
const BORDER = "#E5E5E7";
const SERIF = '"Playfair Display", Georgia, serif';
const SANS = 'Inter, system-ui, sans-serif';

const DIFFERENTIATORS: { n: string; label: string; desc: string }[] = [
  { n: "01", label: "Methodology-native", desc: "M-PACT lives inside the workflow." },
  { n: "02", label: "Coach embedded", desc: "In the plan. In the opportunity. In the review." },
  { n: "03", label: "Pipeline-first", desc: "Live evidence, not slide-deck theory." },
  { n: "04", label: "Built for operators", desc: "Sales leaders, AEs, revenue ops." },
  { n: "05", label: "Three-layer stack", desc: "Mi6 Group + M-PACT + Attilius." },
];

const NAV_LINKS: { label: string; href: string }[] = [
  { label: "Product", href: "#/methodology" },
  { label: "Methodology", href: "#/methodology" },
  { label: "Pricing", href: "#/methodology" },
];

export default function Landing() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: BG,
        color: BLACK,
        fontFamily: SANS,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Top nav ─────────────────────────────────────────────────────── */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "22px 40px",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <img
          src="/brand/mi6-attilius-horizontal.png"
          alt="Mi6 Group — Attilius"
          style={{ height: 44, width: "auto", display: "block" }}
        />
        <nav style={{ display: "flex", alignItems: "center", gap: 34, flexWrap: "wrap" }}>
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              style={{ fontSize: 15, color: BODY, textDecoration: "none" }}
            >
              {l.label}
            </a>
          ))}
          <a href="#/login" style={{ fontSize: 15, color: BLACK, textDecoration: "none" }}>
            Sign in
          </a>
        </nav>
      </header>

      {/* ── Hero grid ───────────────────────────────────────────────────── */}
      <main
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 0.85fr)",
          gap: 48,
          alignItems: "center",
          padding: "40px 40px 64px",
          maxWidth: 1440,
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        {/* Left column — hero copy */}
        <section>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: RED,
              marginBottom: 28,
            }}
          >
            The Sales Intelligence Platform
          </div>

          <h1
            style={{
              fontFamily: SERIF,
              fontWeight: 700,
              color: BLACK,
              fontSize: "clamp(52px, 8vw, 108px)",
              lineHeight: 1.02,
              margin: 0,
            }}
          >
            From "I think"
            <br />
            to "I know."
          </h1>

          <div
            style={{
              width: 140,
              height: 6,
              background: RED,
              margin: "28px 0 24px",
              borderRadius: 2,
            }}
          />

          <div
            style={{
              fontFamily: SERIF,
              fontSize: "clamp(26px, 3vw, 34px)",
              color: BODY,
              marginBottom: 28,
            }}
          >
            From guesswork to certainty.
          </div>

          <p
            style={{
              fontSize: 18,
              lineHeight: 1.65,
              color: BODY,
              maxWidth: 640,
              margin: "0 0 36px",
            }}
          >
            The methodology-native platform where the coach starts at the plan,
            not the opportunity. Attilius carries an operator's expertise into your market
            and your offerings — from the sales plan through every checkpoint of every opportunity.
          </p>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <a
              href="mailto:support@mi6group.com?subject=Request%20a%20demo%20-%20Attilius"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "15px 30px",
                background: RED,
                color: WHITE,
                fontSize: 16,
                fontWeight: 600,
                textDecoration: "none",
                borderRadius: 2,
              }}
            >
              Request a demo
            </a>
            <a
              href="#/methodology"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "15px 30px",
                background: "transparent",
                color: BLACK,
                fontSize: 16,
                fontWeight: 600,
                textDecoration: "none",
                border: `1px solid ${BLACK}`,
                borderRadius: 2,
              }}
            >
              See the methodology
            </a>
          </div>
        </section>

        {/* Right column — Why Attilius card */}
        <aside
          style={{
            background: WHITE,
            border: `1px solid ${BORDER}`,
            borderRadius: 10,
            padding: "36px 34px",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: RED,
              marginBottom: 14,
            }}
          >
            Why Attilius
          </div>
          <h2
            style={{
              fontFamily: SERIF,
              fontWeight: 700,
              color: BLACK,
              fontSize: "clamp(26px, 2.4vw, 34px)",
              lineHeight: 1.12,
              margin: "0 0 26px",
            }}
          >
            Five things no other sales platform does.
          </h2>

          <div>
            {DIFFERENTIATORS.map((d, i) => (
              <div
                key={d.n}
                style={{
                  display: "flex",
                  gap: 18,
                  padding: "18px 0",
                  borderTop: i === 0 ? "none" : `1px solid ${BORDER}`,
                }}
              >
                <span
                  style={{
                    color: RED,
                    fontSize: 15,
                    fontWeight: 700,
                    minWidth: 24,
                  }}
                >
                  {d.n}
                </span>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: BLACK, marginBottom: 4 }}>
                    {d.label}
                  </div>
                  <div style={{ fontSize: 14, color: BODY }}>{d.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </main>

      {/* ── Footer strip ────────────────────────────────────────────────── */}
      <footer
        style={{
          padding: "20px 40px",
          fontSize: 13,
          color: MUTED,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        aip.mi6group.com · Mi6 Group + M-PACT + Attilius
      </footer>
    </div>
  );
}
